import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isAxiosError } from 'services/axios';

import {
  mapConversionInconsistency,
  normalizeInconsistencyResponse,
} from './conversionUtils';
import {
  BUDGET_IDENTIFICATION_TIMEOUT_MESSAGE,
  duplicateBudgetAsSienge,
  exportBudgetConversionInconsistencies,
  fetchBudgetConversionInconsistencies,
  INVALID_DUPLICATE_RESPONSE_MESSAGE,
  waitForBudgetIdentification,
} from './service';
import { BudgetConversionInconsistency, ConversionLoadStatus } from './types';

const DEFAULT_PER_PAGE = 20;

type UseBudgetConversionToSiengeParams = {
  budgetId: number;
};

type ExportRequestResult = { ok: true } | { ok: false; errorMessage: string };

type ConversionRequestResult =
  | { ok: true; duplicatedBudgetId: number }
  | { ok: false; errorMessage: string };

type ConversionRequestOptions = {
  waitForIdentification?: boolean;
};

type UseBudgetConversionToSiengeResult = {
  status: ConversionLoadStatus;
  inconsistencies: BudgetConversionInconsistency[];
  page: number;
  perPage: number;
  lastPage: number;
  total: number;
  sort: string | null;
  loadErrorMessage: string | null;
  conversionErrorMessage: string | null;
  isExporting: boolean;
  isConverting: boolean;
  setPage: (nextPage: number) => void;
  updateSort: (sort: string) => void;
  reload: () => Promise<void>;
  requestExport: () => Promise<ExportRequestResult>;
  requestConversion: (
    options?: ConversionRequestOptions,
  ) => Promise<ConversionRequestResult>;
};

type ErrorWithResponse = {
  code?: string;
  message?: string;
  response?: {
    data?: {
      message?: string;
    };
  };
};

const getErrorMessage = (
  error: unknown,
  fallbackMessage: string,
  timeoutMessage: string,
): string => {
  const errorData = error as ErrorWithResponse;
  const normalizedMessage = errorData?.message?.toLowerCase();
  const isTimeout =
    errorData?.code === 'ECONNABORTED' ||
    normalizedMessage?.includes('timeout');

  if (isTimeout) {
    return timeoutMessage;
  }

  if (isAxiosError(error)) {
    return error.response?.data?.message || fallbackMessage;
  }

  return fallbackMessage;
};

const logError = (stage: 'load' | 'export' | 'convert', error: unknown): void =>
  // eslint-disable-next-line no-console
  console.error(`[BudgetConversionToSienge][${stage}]`, error);

export const useBudgetConversionToSienge = ({
  budgetId,
}: UseBudgetConversionToSiengeParams): UseBudgetConversionToSiengeResult => {
  const [status, setStatus] = useState<ConversionLoadStatus>('loading');
  const [inconsistencies, setInconsistencies] = useState<
    BudgetConversionInconsistency[]
  >([]);

  const [page, setPageState] = useState(1);
  const [perPage] = useState(DEFAULT_PER_PAGE);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<string | null>(null);

  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [conversionErrorMessage, setConversionErrorMessage] = useState<
    string | null
  >(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const loadRequestIdRef = useRef(0);

  const loadData = useCallback(async (): Promise<void> => {
    loadRequestIdRef.current += 1;
    const requestId = loadRequestIdRef.current;

    setStatus('loading');
    setLoadErrorMessage(null);

    try {
      const responseRaw = await fetchBudgetConversionInconsistencies(budgetId, {
        page,
        per_page: perPage,
        ...(sort ? { sort } : {}),
      });
      const response = normalizeInconsistencyResponse(responseRaw);

      if (requestId !== loadRequestIdRef.current) {
        return;
      }

      const normalizedData = response.data.map(mapConversionInconsistency);
      const nextLastPage = response.meta?.last_page || response.last_page || 1;
      const nextTotal =
        response.meta?.total || response.total || normalizedData.length;

      setInconsistencies(normalizedData);
      setLastPage(nextLastPage);
      setTotal(nextTotal);
      setStatus(normalizedData.length > 0 ? 'success' : 'empty');
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }

      logError('load', error);
      setInconsistencies([]);
      setLastPage(1);
      setTotal(0);
      setStatus('error');
      setLoadErrorMessage(
        getErrorMessage(
          error,
          'Não foi possível carregar as inconsistências do orçamento.',
          'A consulta de inconsistências excedeu o tempo esperado. Tente novamente em instantes.',
        ),
      );
    }
  }, [budgetId, page, perPage, sort]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (page > lastPage) {
      setPageState(1);
    }
  }, [lastPage, page]);

  const setPage = useCallback((nextPage: number) => {
    setPageState(nextPage > 0 ? nextPage : 1);
  }, []);

  const updateSort = useCallback((nextSort: string) => {
    const normalizedSort = nextSort.trim();

    setSort(normalizedSort || null);
    setPageState(1);
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    await loadData();
  }, [loadData]);

  const requestExport = useCallback(async (): Promise<ExportRequestResult> => {
    if (isExporting) {
      return { ok: true };
    }

    setIsExporting(true);

    try {
      await exportBudgetConversionInconsistencies(budgetId);
      return { ok: true };
    } catch (error) {
      logError('export', error);

      return {
        ok: false,
        errorMessage: getErrorMessage(
          error,
          'Não foi possível gerar o relatório de inconsistências. Tente novamente em instantes.',
          'A exportação excedeu o tempo esperado. Tente novamente em instantes.',
        ),
      };
    } finally {
      setIsExporting(false);
    }
  }, [budgetId, isExporting]);

  const requestConversion = useCallback(
    async (
      options?: ConversionRequestOptions,
    ): Promise<ConversionRequestResult> => {
      if (isConverting) {
        return {
          ok: false,
          errorMessage: 'A conversão já está em andamento.',
        };
      }

      setIsConverting(true);
      setConversionErrorMessage(null);

      try {
        const shouldWaitForIdentification =
          options?.waitForIdentification !== false;
        const duplicatedBudgetId = await duplicateBudgetAsSienge(budgetId);
        if (shouldWaitForIdentification) {
          await waitForBudgetIdentification(duplicatedBudgetId);
        }

        return {
          ok: true,
          duplicatedBudgetId,
        };
      } catch (error) {
        logError('convert', error);

        const errorMessage =
          (error instanceof Error &&
            error.message === INVALID_DUPLICATE_RESPONSE_MESSAGE &&
            'A resposta da API foi inesperada ao converter o orçamento.') ||
          (error instanceof Error &&
            error.message === BUDGET_IDENTIFICATION_TIMEOUT_MESSAGE &&
            'O novo orçamento ainda está sendo preparado. Tente novamente em instantes.') ||
          getErrorMessage(
            error,
            'Não foi possível converter o orçamento neste momento.',
            'A conversão excedeu o tempo esperado. Tente novamente em instantes.',
          );

        setConversionErrorMessage(errorMessage);

        return {
          ok: false,
          errorMessage,
        };
      } finally {
        setIsConverting(false);
      }
    },
    [budgetId, isConverting],
  );

  return useMemo(() => {
    return {
      status,
      inconsistencies,
      page,
      perPage,
      lastPage,
      total,
      sort,
      loadErrorMessage,
      conversionErrorMessage,
      isExporting,
      isConverting,
      setPage,
      updateSort,
      reload,
      requestExport,
      requestConversion,
    };
  }, [
    status,
    inconsistencies,
    page,
    perPage,
    lastPage,
    total,
    sort,
    loadErrorMessage,
    conversionErrorMessage,
    isExporting,
    isConverting,
    setPage,
    updateSort,
    reload,
    requestExport,
    requestConversion,
  ]);
};

export default useBudgetConversionToSienge;
